import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost, streamNdjson } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Icon } from "../components/Icons";
import { Spinner } from "../components/Loaders";
import { PageIntro } from "../components/Visual";

type ChatRole = "user" | "assistant";

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
}

interface ChatSession {
  id: string;
  title: string;
  updatedAt: string;
  messages: ChatMessage[];
}

type BloomyEvent =
  | { type: "context"; sources: string[] }
  | { type: "token"; text: string }
  | { type: "done" }
  | { type: "error"; message: string };
/** If the chat log is scrolled within this many px of the bottom, new tokens follow automatically. */
const BOTTOM_FOLLOW_PX = 100;

const FALLBACK_STARTER_PROMPTS = [
  "What can I build from my collection right now?",
  "Compare my saved decks and tell me what archetypes I am missing.",
  "Find community decks similar to my latest build.",
  "What cards support a Dinosaur ramp plan?",
  "What Arena archetype should I try if I like tempo decks?",
  "Which cards in my collection look best for a midrange shell?",
];
const WELCOME_MESSAGE: ChatMessage = {
  id: "welcome",
  role: "assistant",
  content:
    "Hi, I'm Bloomy. Ask me about your cards, saved decks, community decks, or MTG Arena knowledge and I'll pull in the most relevant local context before answering.",
};
const MAX_STORED_MESSAGES = 80;
const MAX_STORED_SESSIONS = 20;

function newId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function historyStorageKey(uid: string) {
  return `foilbloom:bloomy-history:${uid}`;
}

function sessionsStorageKey(uid: string) {
  return `foilbloom:bloomy-sessions:${uid}`;
}

function activeSessionStorageKey(uid: string) {
  return `foilbloom:bloomy-active-session:${uid}`;
}

function normalizeStoredMessages(value: unknown): ChatMessage[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((message): message is ChatMessage => {
      if (!message || typeof message !== "object") return false;
      const row = message as Record<string, unknown>;
      return (
        typeof row.id === "string" &&
        (row.role === "user" || row.role === "assistant") &&
        typeof row.content === "string"
      );
    })
    .map((message) => ({
      id: message.id,
      role: message.role,
      content: message.content,
    }))
    .slice(-MAX_STORED_MESSAGES);
}

function messagesContentEqual(a: ChatMessage[], b: ChatMessage[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (x.id !== y.id || x.role !== y.role || x.content !== y.content) return false;
  }
  return true;
}

function buildSessionTitle(messages: ChatMessage[]) {
  const firstUserMessage = messages.find((message) => message.role === "user")?.content.trim();
  if (!firstUserMessage) return "New Bloomy chat";
  return firstUserMessage.length > 52 ? `${firstUserMessage.slice(0, 49).trim()}...` : firstUserMessage;
}

function normalizeStoredSessions(value: unknown): ChatSession[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((session): session is ChatSession => {
      if (!session || typeof session !== "object") return false;
      const row = session as Record<string, unknown>;
      return typeof row.id === "string" && Array.isArray(row.messages);
    })
    .map((session) => {
      const messages = normalizeStoredMessages(session.messages);
      return {
        id: session.id,
        title: typeof session.title === "string" && session.title.trim()
          ? session.title.trim()
          : buildSessionTitle(messages),
        updatedAt: typeof session.updatedAt === "string" ? session.updatedAt : new Date().toISOString(),
        messages,
      };
    })
    .filter((session) => session.messages.length > 0)
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
    .slice(0, MAX_STORED_SESSIONS);
}

function shufflePrompts(prompts: string[]) {
  const copy = [...prompts];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

function MarkdownMessage({ content }: { content: string }) {
  const blocks: Array<
    | { type: "paragraph"; lines: string[] }
    | { type: "heading"; text: string }
    | { type: "list"; ordered: boolean; items: string[] }
  > = [];

  for (const rawLine of content.split(/\r?\n/)) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }
    const heading = trimmed.match(/^#{1,6}\s+(.+)$/);
    if (heading) {
      blocks.push({ type: "heading", text: heading[1] });
      continue;
    }
    const bullet = trimmed.match(/^(?:[-*+]|\d+[.)])\s+(.*)$/);
    if (bullet) {
      const ordered = /^\d+[.)]/.test(trimmed);
      const previous = blocks[blocks.length - 1];
      if (previous?.type === "list" && previous.ordered === ordered) {
        previous.items.push(bullet[1]);
      } else {
        blocks.push({ type: "list", ordered, items: [bullet[1]] });
      }
      continue;
    }
    const previous = blocks[blocks.length - 1];
    if (previous?.type === "paragraph") {
      previous.lines.push(trimmed);
    } else {
      blocks.push({ type: "paragraph", lines: [trimmed] });
    }
  }

  return (
    <div className="bloomy-markdown">
      {blocks.map((block, index) => {
        if (block.type === "heading") {
          return <h3 key={index}>{renderInlineMarkdown(block.text)}</h3>;
        }
        if (block.type === "list") {
          const ListTag = block.ordered ? "ol" : "ul";
          return (
            <ListTag key={index}>
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInlineMarkdown(item)}</li>
              ))}
            </ListTag>
          );
        }
        return (
          <p key={index}>
            {block.lines.map((line, lineIndex) => (
              <span key={lineIndex}>
                {lineIndex > 0 ? <br /> : null}
                {renderInlineMarkdown(line)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

function ThinkingIndicator() {
  return (
    <div className="bloomy-thinking" aria-label="Bloomy is thinking">
      <span>Bloomy is thinking</span>
      <span className="bloomy-thinking-dots" aria-hidden>
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

export function Bloomy() {
  const { firebaseUser, profile, refreshProfile } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyReady, setHistoryReady] = useState(false);
  const [starterPrompts, setStarterPrompts] = useState(() =>
    shufflePrompts(FALLBACK_STARTER_PROMPTS).slice(0, 4),
  );
  const [upgradeBusy, setUpgradeBusy] = useState<"pro" | "plus" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const chatLogRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  const onChatLogScroll = () => {
    const el = chatLogRef.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = fromBottom < BOTTOM_FOLLOW_PX;
  };

  useEffect(() => {
    const el = chatLogRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight - el.clientHeight;
  }, [messages, busy]);

  useEffect(() => {
    return () => abortRef.current?.();
  }, []);

  useEffect(() => {
    const uid = firebaseUser?.uid;
    setHistoryReady(false);
    if (!uid) {
      setMessages([WELCOME_MESSAGE]);
      setChatSessions([]);
      setActiveSessionId(null);
      return;
    }
    try {
      const rawSessions = window.localStorage.getItem(sessionsStorageKey(uid));
      let storedSessions = normalizeStoredSessions(rawSessions ? JSON.parse(rawSessions) : []);
      if (!storedSessions.length) {
        const legacyRaw = window.localStorage.getItem(historyStorageKey(uid));
        const legacyMessages = normalizeStoredMessages(legacyRaw ? JSON.parse(legacyRaw) : []);
        if (legacyMessages.length) {
          storedSessions = [{
            id: newId(),
            title: buildSessionTitle(legacyMessages),
            updatedAt: new Date().toISOString(),
            messages: legacyMessages,
          }];
          window.localStorage.removeItem(historyStorageKey(uid));
        }
      }
      const storedActiveId = window.localStorage.getItem(activeSessionStorageKey(uid));
      const active = storedSessions.find((session) => session.id === storedActiveId) ?? storedSessions[0] ?? null;
      setChatSessions(storedSessions);
      setActiveSessionId(active?.id ?? null);
      setMessages(active ? [WELCOME_MESSAGE, ...active.messages] : [WELCOME_MESSAGE]);
    } catch {
      setMessages([WELCOME_MESSAGE]);
      setChatSessions([]);
      setActiveSessionId(null);
    } finally {
      setHistoryReady(true);
    }
  }, [firebaseUser?.uid]);

  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid || !historyReady) return;
    const stored = messages.filter((message) => message.id !== "welcome").slice(-MAX_STORED_MESSAGES);
    if (!stored.length) return;
    const nextSessionId = activeSessionId ?? newId();
    if (!activeSessionId) {
      setActiveSessionId(nextSessionId);
    }
    setChatSessions((prev) => {
      const prior = prev.find((s) => s.id === nextSessionId);
      if (prior && messagesContentEqual(prior.messages, stored)) {
        const nextTitle = buildSessionTitle(stored);
        if (prior.title !== nextTitle) {
          return prev.map((s) => (s.id === nextSessionId ? { ...s, title: nextTitle } : s));
        }
        // Switched to this session or effect re-ran on same snapshot — keep list order and updatedAt.
        return prev;
      }
      const updatedSession: ChatSession = {
        id: nextSessionId,
        title: buildSessionTitle(stored),
        updatedAt: new Date().toISOString(),
        messages: stored,
      };
      return [updatedSession, ...prev.filter((s) => s.id !== nextSessionId)].slice(0, MAX_STORED_SESSIONS);
    });
  }, [activeSessionId, firebaseUser?.uid, historyReady, messages]);

  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid || !historyReady) return;
    try {
      if (chatSessions.length) {
        window.localStorage.setItem(sessionsStorageKey(uid), JSON.stringify(chatSessions));
      } else {
        window.localStorage.removeItem(sessionsStorageKey(uid));
      }
      if (activeSessionId) {
        window.localStorage.setItem(activeSessionStorageKey(uid), activeSessionId);
      } else {
        window.localStorage.removeItem(activeSessionStorageKey(uid));
      }
    } catch {
      // localStorage can fail in private mode or when full; chat still works in-memory.
    }
  }, [activeSessionId, chatSessions, firebaseUser?.uid, historyReady]);

  useEffect(() => {
    let alive = true;
    apiGet<{ prompts: string[] }>("/api/knowledge/prompts?limit=4")
      .then(({ prompts }) => {
        if (!alive || !prompts?.length) return;
        setStarterPrompts(shufflePrompts(prompts).slice(0, 4));
      })
      .catch(() => {
        if (alive) {
          setStarterPrompts(shufflePrompts(FALLBACK_STARTER_PROMPTS).slice(0, 4));
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const sendMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    if (!hasBloomyAccess || quotaBlocked) return;

    stickToBottomRef.current = true;
    const userMessage: ChatMessage = { id: newId(), role: "user", content: trimmed };
    const assistantId = newId();
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
    };
    const history = [...messages, userMessage]
      .filter((m) => m.id !== "welcome")
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInput("");
    setError(null);
    setBusy(true);

    const stream = streamNdjson<BloomyEvent>({
      path: "/api/bloomy/chat",
      body: { message: trimmed, history },
      onEvent: (event) => {
        if (event.type === "context") {
          return;
        }
        if (event.type === "token") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: m.content + event.text } : m,
            ),
          );
          return;
        }
        if (event.type === "error") {
          setError(event.message);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: m.content || "I hit an error while answering." }
                : m,
            ),
          );
          setBusy(false);
          return;
        }
        if (event.type === "done") {
          setBusy(false);
          abortRef.current = null;
          void refreshProfile();
        }
      },
      onError: (message) => {
        setError(message);
        setBusy(false);
        void refreshProfile();
      },
    });
    abortRef.current = stream.abort;
    void stream.promise.finally(() => {
      setBusy(false);
      abortRef.current = null;
    });
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    sendMessage(input);
  };

  const stop = () => {
    abortRef.current?.();
    abortRef.current = null;
    setBusy(false);
  };

  const startUnsavedSession = () => {
    if (busy) return;
    setActiveSessionId(null);
    setMessages([WELCOME_MESSAGE]);
  };

  const openChatSession = (sessionId: string) => {
    if (busy || sessionId === activeSessionId) return;
    const session = chatSessions.find((item) => item.id === sessionId);
    if (!session) return;
    setActiveSessionId(session.id);
    setMessages([WELCOME_MESSAGE, ...session.messages]);
  };

  const deleteChatSession = (sessionId: string) => {
    if (busy) return;
    const nextSessions = chatSessions.filter((session) => session.id !== sessionId);
    setChatSessions(nextSessions);
    if (sessionId !== activeSessionId) return;
    const nextActive = nextSessions[0] ?? null;
    setActiveSessionId(nextActive?.id ?? null);
    setMessages(nextActive ? [WELCOME_MESSAGE, ...nextActive.messages] : [WELCOME_MESSAGE]);
  };

  const onUpgrade = async (plan: "pro" | "plus") => {
    setError(null);
    setUpgradeBusy(plan);
    try {
      const { url } = await apiPost<{ url: string }>("/api/billing/checkout", { plan });
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUpgradeBusy(null);
    }
  };

  const isPlus = profile?.plan === "plus";
  const hasBloomyAccess = profile?.plan === "pro" || isPlus;
  const remaining = profile?.bloomyMessagesRemaining;
  const resetText = profile?.bloomyResetAt
    ? new Date(profile.bloomyResetAt).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      })
    : null;
  const quotaBlocked = profile?.plan === "pro" && remaining === 0;

  return (
    <main className="page bloomy-page">
      <PageIntro
        eyebrow="Bloomy chat"
        icon="magic"
        title={<>Ask <span className="grad">Bloomy.</span></>}
        body="Chat with Foil Bloom's AI about your collection, saved decks, public brews, card data, and the local MTG Arena knowledge base."
        actions={
          <>
            <Link to="/collection" className="topbar-btn"><Icon name="archive" size={16} />Collection</Link>
            <Link to="/decks" className="topbar-btn"><Icon name="cards" size={16} />Saved decks</Link>
          </>
        }
      />

      {error && <div className="error-banner">{error}</div>}

      {!hasBloomyAccess && (
        <section className="bloomy-paywall">
          <div>
            <h2>Bloomy requires Unlimited.</h2>
            <p>
              Upgrade when you&apos;re ready to chat about your collection, decks, and cards.
            </p>
          </div>
          <button
            type="button"
            className="secondary"
            onClick={() => onUpgrade("pro")}
            disabled={upgradeBusy !== null}
          >
            {upgradeBusy === "pro" ? (
              <>
                <Spinner className="button-spinner" />
                Opening checkout...
              </>
            ) : (
              "Upgrade to Unlimited"
            )}
          </button>
        </section>
      )}

      {quotaBlocked && (
        <section className="bloomy-paywall bloomy-paywall--plus">
          <div>
            <h2>Bloomy messages refresh soon.</h2>
            <p>
              You&apos;re out for this window{resetText ? ` until around ${resetText}` : ""}.
              Plus removes the chat limit.
            </p>
          </div>
          <button
            type="button"
            className="secondary"
            onClick={() => onUpgrade("plus")}
            disabled={upgradeBusy !== null}
          >
            {upgradeBusy === "plus" ? (
              <>
                <Spinner className="button-spinner" />
                Opening checkout...
              </>
            ) : (
              "Upgrade to Unlimited Plus"
            )}
          </button>
        </section>
      )}

      <div
        className={`bloomy-workspace${!hasBloomyAccess || quotaBlocked ? " bloomy-workspace--locked" : ""}`}
      >
        {hasBloomyAccess && !quotaBlocked && (
          <aside className="bloomy-rail" aria-label="Bloomy chat history">
            <div className="bloomy-rail-top">
              <button
                type="button"
                className="primary bloomy-rail-new"
                onClick={startUnsavedSession}
                disabled={busy}
              >
                <Icon name="sparkles" size={16} />
                New chat
              </button>
            </div>
            <div className="bloomy-rail-list" role="list">
              {chatSessions.length ? (
                chatSessions.map((session) => (
                  <div key={session.id} className="bloomy-rail-item" role="listitem">
                    <button
                      type="button"
                      className={
                        "bloomy-rail-link" + (activeSessionId === session.id ? " is-active" : "")
                      }
                      onClick={() => openChatSession(session.id)}
                      disabled={busy}
                      title={session.title}
                    >
                      {session.title}
                    </button>
                    <button
                      type="button"
                      className="bloomy-rail-delete"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        deleteChatSession(session.id);
                      }}
                      disabled={busy}
                      aria-label={`Delete chat: ${session.title}`}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                ))
              ) : (
                <p className="bloomy-rail-empty">Chats you start will show up here.</p>
              )}
            </div>
            <p className="bloomy-rail-foot">Saved in this browser only.</p>
          </aside>
        )}

        <section
          className="bloomy-shell"
          aria-label="Bloomy chat"
          aria-disabled={!hasBloomyAccess || quotaBlocked}
        >
          {hasBloomyAccess && (
            <div className="bloomy-quota">
              {isPlus ? (
                <span>Plus: unrestricted chat</span>
              ) : (
                <span>
                  {remaining ?? 10} message{remaining === 1 ? "" : "s"} left
                  {resetText ? `, resets around ${resetText}` : ""}
                </span>
              )}
            </div>
          )}
          <div
            className="bloomy-chat-log"
            ref={chatLogRef}
            onScroll={onChatLogScroll}
          >
            {messages.map((message) => (
              <article key={message.id} className={`bloomy-message bloomy-message--${message.role}`}>
                <div className={`bloomy-avatar bloomy-avatar--${message.role}`} aria-hidden>
                  {message.role === "assistant" ? (
                    <img
                      src="/bloomy.png"
                      alt=""
                      width={34}
                      height={34}
                      decoding="async"
                      draggable={false}
                    />
                  ) : (
                    <Icon name="user" size={16} />
                  )}
                </div>
                <div className="bloomy-bubble">
                  <div className="bloomy-message-label">
                    {message.role === "assistant" ? "Bloomy" : "You"}
                  </div>
                  <div className="bloomy-message-text">
                    {message.content ? (
                      <MarkdownMessage content={message.content} />
                    ) : busy && message.role === "assistant" ? (
                      <ThinkingIndicator />
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="bloomy-starters" aria-label="Example prompts">
            {starterPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="bloomy-starter"
                disabled={busy || !hasBloomyAccess || quotaBlocked}
                onClick={() => sendMessage(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>

          <form className="bloomy-composer" onSubmit={onSubmit}>
            <textarea
              rows={3}
              value={input}
              placeholder="Ask Bloomy about a card, your collection, saved decks, or the community metagame..."
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage(input);
                }
              }}
              disabled={busy || !hasBloomyAccess || quotaBlocked}
            />
            <div className="bloomy-composer-actions">
              {busy ? (
                <button type="button" className="secondary" onClick={stop}>
                  Stop
                </button>
              ) : null}
              <button
                type="submit"
                className="primary"
                disabled={busy || !input.trim() || !hasBloomyAccess || quotaBlocked}
              >
                {busy ? (
                  <>
                    <Spinner className="button-spinner" />
                    Bloomy is thinking
                  </>
                ) : (
                  <>
                    <Icon name="magic" size={16} />
                    Send
                  </>
                )}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}

